import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Snackbar,
  TextField,
} from '@mui/material';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useEffect, useState } from 'react';

import {
  getApiErrorMessage,
  getMixDelivery,
  updateMixDeliveryStatus,
  confirmMixDeliveryReview,
} from '../../../../api/mix-deliveriesApi';

import {
  MIX_DELIVERY_STATUS,
  MIX_DELIVERY_STATUS_LABELS,
  MIX_STOCK_STATUS,
  MIX_STOCK_STATUS_LABELS,
} from '../types/mix-deliveries.types';

import MixStatusChangeDialog from './MixStatusChangeDialog';

export default function MixDeliveryDetailsDialog({
  deliveryId,
  open,
  onClose,
  onStatusChanged,
  userRole,
}) {
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState(null);
  const [targetStatus, setTargetStatus] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  const [reviewComment, setReviewComment] = useState('');

  const [reviewLoading, setReviewLoading] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
  });

  useEffect(() => {
    if (!open || !deliveryId) {
      return;
    }

    let active = true;

    async function loadDelivery() {
      setLoading(true);
      setError(null);

      try {
        const result = await getMixDelivery(deliveryId);

        if (active) {
          setDelivery(result);
        }
      } catch (requestError) {
        if (active) {
          setError(getApiErrorMessage(requestError));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadDelivery();

    return () => {
      active = false;
    };
  }, [deliveryId, open]);

  const handleRequestStatusChange = (status) => {
    setTargetStatus(status);
  };

  const handleConfirmStatusChange = async (comment) => {
    if (!deliveryId || !targetStatus) {
      return;
    }

    setStatusLoading(true);
    setError(null);

    try {
      const result = await updateMixDeliveryStatus(
        deliveryId,
        targetStatus,
        comment,
      );

      setDelivery(result);
      setTargetStatus(null);

      setNotification({
        open: true,
        message: 'Status dostawy został zaktualizowany.',
      });

      onStatusChanged?.(result);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!statusLoading && !reviewLoading) {
      onClose?.();
    }
  };

  // const items = delivery?.items ?? [];

  // const mixItems = items.filter((item) => isMixItem(item));

  // const fullPalletItems = items.filter((item) => !isMixItem(item));
  const isOperator = String(userRole ?? '').toUpperCase() === 'OPERATOR';
  const normalizedRole = String(userRole ?? '').toUpperCase();

  const canReviewPreparedChange =
    normalizedRole === 'ADMIN' || normalizedRole === 'KONTROLER';

  const allItems = delivery?.items ?? [];

  const items = isOperator
    ? allItems.filter((item) => isMixItem(item))
    : allItems;

  const mixItems = items.filter((item) => isMixItem(item));

  const fullPalletItems = items.filter((item) => !isMixItem(item));

  const handleConfirmReview = async () => {
    if (!deliveryId) {
      return;
    }

    setReviewLoading(true);
    setError(null);

    try {
      const result = await confirmMixDeliveryReview(deliveryId, reviewComment);

      setDelivery(result);

      setReviewDialogOpen(false);
      setReviewComment('');

      setNotification({
        open: true,
        message: 'Zmiana została potwierdzona.',
      });

      onStatusChanged?.(result);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setReviewLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="xl">
        <DialogTitle>
          {delivery
            ? `Dostawa ${delivery.deliveryNumber}`
            : 'Szczegóły dostawy'}
        </DialogTitle>

        <DialogContent dividers>
          {loading && (
            <Box
              sx={{
                py: 5,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && delivery && (
            <Stack spacing={3}>
              {delivery.requiresReview && (
                <ChangeReviewAlert delivery={delivery} />
              )}
              <DeliveryHeader delivery={delivery} isOperator={isOperator} />

              <Divider />

              <Box>
                {/* <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                  sx={{ mb: 1.5 }}
                >
                  <Typography variant="h6">Pozycje dostawy</Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      color="warning"
                      label={`Pozycje miksowe: ${mixItems.length}`}
                    />

                    <Chip
                      size="small"
                      label={`Pozycje pełnopaletowe: ${fullPalletItems.length}`}
                    />

                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Łącznie: ${items.length}`}
                    />
                  </Stack>
                </Stack> */}
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {isOperator ? 'Pozycje miksowe' : 'Pozycje dostawy'}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 1.5 }}
                >
                  <Chip
                    size="small"
                    color="warning"
                    label={`Pozycje miksowe: ${mixItems.length}`}
                  />

                  {!isOperator && (
                    <>
                      <Chip
                        size="small"
                        label={`Pozycje pełnopaletowe: ${fullPalletItems.length}`}
                      />

                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Łącznie: ${items.length}`}
                      />
                    </>
                  )}
                </Stack>
                <DeliveryItemsTable items={items} />
              </Box>

              {delivery.events?.length > 0 && (
                <>
                  <Divider />

                  <Box>
                    <Typography variant="h6" sx={{ mb: 1.5 }}>
                      Historia
                    </Typography>

                    <Stack spacing={1}>
                      {delivery.events.map((event) => (
                        <Paper
                          key={event.id}
                          variant="outlined"
                          sx={{ p: 1.5 }}
                        >
                          <Typography variant="body2">
                            {getEventLabel(event)}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(event.createdAt)}
                          </Typography>

                          {event.comment && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {event.comment}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {delivery && (
            <StatusActions
              currentStatus={delivery.status}
              requiresReview={delivery.requiresReview}
              loading={statusLoading || reviewLoading}
              canReviewPreparedChange={canReviewPreparedChange}
              onStatusChange={handleRequestStatusChange}
              onConfirmReview={() => setReviewDialogOpen(true)}
            />
          )}

          <Button onClick={handleDialogClose} disabled={statusLoading}>
            Zamknij
          </Button>
        </DialogActions>
      </Dialog>
      <MixStatusChangeDialog
        open={Boolean(targetStatus)}
        currentStatus={delivery?.status}
        targetStatus={targetStatus}
        loading={statusLoading}
        onClose={() => setTargetStatus(null)}
        onConfirm={handleConfirmStatusChange}
      />

      <Dialog
        open={reviewDialogOpen}
        onClose={() => {
          if (!reviewLoading) {
            setReviewDialogOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Potwierdź zmianę dostawy</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              Potwierdzenie oznacza, że aktualna wersja dostawy nie wymaga
              ponownego przygotowania miksu.
            </Alert>

            <TextField
              label="Komentarz"
              multiline
              minRows={3}
              value={reviewComment}
              onChange={(event) => setReviewComment(event.target.value)}
              disabled={reviewLoading}
              inputProps={{
                maxLength: 1000,
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setReviewDialogOpen(false)}
            disabled={reviewLoading}
          >
            Anuluj
          </Button>

          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmReview}
            disabled={reviewLoading}
          >
            {reviewLoading ? 'Zapisywanie...' : 'Potwierdź zmianę'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() =>
          setNotification({
            open: false,
            message: '',
          })
        }
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() =>
            setNotification({
              open: false,
              message: '',
            })
          }
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}

function DeliveryHeader({ delivery, isOperator }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
        },
        gap: 2,
      }}
    >
      <InfoBox label="Numer dostawy" value={delivery.deliveryNumber} />

      <InfoBox label="Data dostawy" value={formatDate(delivery.deliveryDate)} />

      <InfoBox
        label="Odbiorca"
        value={delivery.recipientName || delivery.recipientNumber || '—'}
      />

      <InfoBox label="Liczba pozycji miksowych" value={delivery.mixItemCount} />

      {!isOperator && (
        <InfoBox
          label="Wszystkie pozycje"
          value={delivery.items?.length ?? 0}
        />
      )}

      <InfoBox
        label="Status pracy"
        value={<DeliveryStatusChip status={delivery.status} />}
      />

      <InfoBox
        label="Status zapasu"
        value={<StockStatusChip status={delivery.stockStatus} />}
      />

      <InfoBox label="Rozpoczęto" value={formatDateTime(delivery.startedAt)} />

      <InfoBox
        label="Przygotowano"
        value={formatDateTime(delivery.preparedAt)}
      />

      <InfoBox
        label="Aktualna wersja"
        value={delivery.currentRevisionNumber ?? '—'}
      />

      <InfoBox
        label="Przygotowana wersja"
        value={delivery.preparedRevisionNumber ?? '—'}
      />

      <InfoBox
        label="Kanapkowanie"
        value={
          <Chip
            size="small"
            label={delivery.sandwichingRequired ? 'TAK' : 'NIE'}
            color={delivery.sandwichingRequired ? 'warning' : 'default'}
            variant={delivery.sandwichingRequired ? 'filled' : 'outlined'}
          />
        }
      />
    </Box>
  );
}
function ChangeReviewAlert({ delivery }) {
  const summary = delivery.lastChangeSummary ?? {};

  const chips = [
    {
      label: `Nowe miksy: ${summary.addedMixPositionsCount ?? 0}`,
      visible: Number(summary.addedMixPositionsCount ?? 0) > 0,
    },
    {
      label: `Nowe pełne palety: ${summary.addedFullPalletPositionsCount ?? 0}`,
      visible: Number(summary.addedFullPalletPositionsCount ?? 0) > 0,
    },
    {
      label: `Usunięte pozycje: ${summary.removedPositionsCount ?? 0}`,
      visible: Number(summary.removedPositionsCount ?? 0) > 0,
    },
    {
      label: `Zmienione pozycje: ${
        summary.changedOperationalPositionsCount ?? 0
      }`,
      visible: Number(summary.changedOperationalPositionsCount ?? 0) > 0,
    },
  ].filter((item) => item.visible);

  return (
    <Alert severity="warning" icon={<WarningAmberIcon />}>
      <Stack spacing={1}>
        <Typography fontWeight={700}>
          Dostawa zmieniła się po przygotowaniu miksu
        </Typography>

        <Typography variant="body2">
          Przygotowana wersja: {delivery.preparedRevisionNumber ?? '—'}
          {' · '}
          Aktualna wersja: {delivery.currentRevisionNumber ?? '—'}
        </Typography>

        {chips.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {chips.map((item) => (
              <Chip key={item.label} size="small" label={item.label} />
            ))}
          </Stack>
        )}

        {summary.recipientNumberChanged && (
          <Typography variant="body2">Zmieniono odbiorcę dostawy.</Typography>
        )}

        {summary.sandwichingRequiredChanged && (
          <Typography variant="body2">
            Zmieniono wymaganie kanapkowania.
          </Typography>
        )}

        <DeliveryChangeDetails summary={summary} />
      </Stack>
    </Alert>
  );
}

function DeliveryChangeDetails({ summary }) {
  const addedPositions = summary.addedPositions ?? [];

  const removedPositions = summary.removedPositions ?? [];

  const changedPositions = summary.changedPositions ?? [];

  const hasDetails =
    addedPositions.length > 0 ||
    removedPositions.length > 0 ||
    changedPositions.length > 0;

  if (!hasDetails) {
    return null;
  }

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {addedPositions.length > 0 && (
        <ChangeSection
          title="Dodane pozycje"
          items={addedPositions}
          renderItem={(item) => (
            <PositionSummary
              key={`${item.positionNumber}-${item.materialNumber}`}
              item={item}
              type="added"
            />
          )}
        />
      )}

      {removedPositions.length > 0 && (
        <ChangeSection
          title="Usunięte pozycje"
          items={removedPositions}
          renderItem={(item) => (
            <PositionSummary
              key={`${item.positionNumber}-${item.materialNumber}`}
              item={item}
              type="removed"
            />
          )}
        />
      )}

      {changedPositions.length > 0 && (
        <ChangeSection
          title="Zmienione pozycje"
          items={changedPositions}
          renderItem={(item) => (
            <ChangedPositionSummary
              key={`${item.positionNumber}-${item.materialNumber}`}
              item={item}
            />
          )}
        />
      )}
    </Stack>
  );
}

function ChangeSection({ title, items, renderItem }) {
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
        {title}
      </Typography>

      <Stack spacing={0.75}>{items.map(renderItem)}</Stack>
    </Box>
  );
}

function PositionSummary({ item, type }) {
  const positionType = item.isMixPosition
    ? 'pozycja miksowa'
    : 'pozycja pełnopaletowa';

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 1,
        bgcolor: type === 'added' ? 'success.50' : 'error.50',
      }}
    >
      <Typography variant="body2">
        <strong>Pozycja {item.positionNumber}</strong>
        {' · '}
        materiał {item.materialNumber}
      </Typography>

      <Typography variant="caption" color="text.secondary">
        {positionType}
        {' · '}
        miks: {formatQuantity(item.mixQuantity)}
        {' · '}
        pełne palety: {formatQuantity(item.fullQuantity)}
      </Typography>
    </Box>
  );
}

function DeliveryItemsTable({ items }) {
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        maxWidth: '100%',
        overflowX: 'auto',
      }}
    >
      <Table
        size="small"
        sx={{
          minWidth: 1500,
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Poz.</TableCell>

            <TableCell>Materiał</TableCell>

            <TableCell>Nazwa</TableCell>

            <TableCell>Rodzaj</TableCell>

            <TableCell align="right">Ilość dostawy</TableCell>

            <TableCell align="right">Ilość pełna</TableCell>

            <TableCell align="right">Ilość miksowa</TableCell>

            <TableCell>JM</TableCell>

            <TableCell>Typ KEG</TableCell>

            <TableCell>Skład</TableCell>

            <TableCell align="right">0D20 przed analizą</TableCell>

            <TableCell align="right">Do uzupełnienia</TableCell>

            <TableCell align="right">Brak</TableCell>

            <TableCell>Status zapasu</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {items.map((item) => {
            const mixPosition = isMixItem(item);

            const originalStorageLocation = item.originalStorageLocation ?? '—';

            const recommendedStorageLocation =
              item.recommendedStorageLocation ?? originalStorageLocation;

            return (
              <TableRow key={item.id} hover>
                <TableCell>{item.positionNumber}</TableCell>

                <TableCell>{item.materialNumberSnapshot}</TableCell>

                <TableCell>{item.productNameSnapshot}</TableCell>

                <TableCell>
                  <Chip
                    size="small"
                    color={mixPosition ? 'warning' : 'default'}
                    label={mixPosition ? 'Miks' : 'Pełne palety'}
                  />
                </TableCell>

                <TableCell align="right">
                  {formatQuantity(item.deliveryQuantity)}
                </TableCell>

                <TableCell align="right">
                  {formatQuantity(item.fullQuantity)}
                </TableCell>

                <TableCell align="right">
                  {mixPosition ? (
                    <Typography component="span" fontWeight={700}>
                      {formatQuantity(item.mixQuantity)}
                    </Typography>
                  ) : (
                    '—'
                  )}
                </TableCell>

                <TableCell>{item.unit}</TableCell>

                <TableCell>{item.kegType ?? '—'}</TableCell>

                <TableCell>
                  {originalStorageLocation === recommendedStorageLocation
                    ? originalStorageLocation
                    : `${originalStorageLocation} → ${recommendedStorageLocation}`}
                </TableCell>

                <TableCell align="right">
                  {mixPosition ? formatQuantity(item.pickingStockBefore) : '—'}
                </TableCell>

                <TableCell align="right">
                  {mixPosition
                    ? formatQuantity(item.replenishmentQuantity)
                    : '—'}
                </TableCell>

                <TableCell align="right">
                  {Number(item.shortageQuantity ?? 0) > 0 ? (
                    <Typography
                      component="span"
                      color="error.main"
                      fontWeight={700}
                    >
                      {formatQuantity(item.shortageQuantity)}
                    </Typography>
                  ) : (
                    '—'
                  )}
                </TableCell>

                <TableCell>
                  <StockStatusChip status={item.stockStatus} />
                </TableCell>
              </TableRow>
            );
          })}

          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                Brak pozycji dostawy.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function StatusActions({
  currentStatus,
  requiresReview,
  loading,
  canReviewPreparedChange,
  onStatusChange,
  onConfirmReview,
}) {
  if (loading) {
    return <CircularProgress size={22} />;
  }

  if (currentStatus === MIX_DELIVERY_STATUS.TO_PREPARE) {
    return (
      <>
        <Button
          variant="outlined"
          onClick={() => onStatusChange(MIX_DELIVERY_STATUS.IN_PROGRESS)}
        >
          Rozpocznij przygotowanie
        </Button>

        <Button
          variant="contained"
          color="success"
          onClick={() => onStatusChange(MIX_DELIVERY_STATUS.PREPARED)}
        >
          Oznacz jako przygotowany
        </Button>
      </>
    );
  }

  if (currentStatus === MIX_DELIVERY_STATUS.IN_PROGRESS) {
    return (
      <>
        <Button
          variant="outlined"
          onClick={() => onStatusChange(MIX_DELIVERY_STATUS.TO_PREPARE)}
        >
          Cofnij do przygotowania
        </Button>

        <Button
          variant="contained"
          color="success"
          onClick={() => onStatusChange(MIX_DELIVERY_STATUS.PREPARED)}
        >
          Oznacz jako przygotowany
        </Button>
      </>
    );
  }

  /*
   * PREPARED.
   *
   * Operator nie może ponownie
   * otwierać przygotowanej dostawy.
   */
  if (!canReviewPreparedChange) {
    return null;
  }

  return (
    <>
      {requiresReview && (
        <Button variant="contained" color="warning" onClick={onConfirmReview}>
          Potwierdź zmianę
        </Button>
      )}

      <Button
        variant="outlined"
        color="warning"
        onClick={() => onStatusChange(MIX_DELIVERY_STATUS.TO_PREPARE)}
      >
        Otwórz ponownie
      </Button>
    </>
  );
}

function InfoBox({ label, value }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        minWidth: 0,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>

      <Box sx={{ mt: 0.5 }}>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Typography fontWeight={600}>{value}</Typography>
        ) : (
          value
        )}
      </Box>
    </Paper>
  );
}

export function DeliveryStatusChip({ status }) {
  const colorMap = {
    [MIX_DELIVERY_STATUS.TO_PREPARE]: 'default',
    [MIX_DELIVERY_STATUS.IN_PROGRESS]: 'info',
    [MIX_DELIVERY_STATUS.PREPARED]: 'success',
  };

  return (
    <Chip
      size="small"
      color={colorMap[status] ?? 'default'}
      label={MIX_DELIVERY_STATUS_LABELS[status] ?? status}
    />
  );
}

export function StockStatusChip({ status }) {
  const colorMap = {
    [MIX_STOCK_STATUS.AVAILABLE]: 'success',
    [MIX_STOCK_STATUS.AVAILABLE_IN_PICKING_AREA]: 'success',
    [MIX_STOCK_STATUS.REPLENISHMENT_REQUIRED]: 'warning',
    [MIX_STOCK_STATUS.STORAGE_LOCATION_CHANGE_REQUIRED]: 'warning',
    [MIX_STOCK_STATUS.STOCK_SHORTAGE]: 'error',
  };

  return (
    <Chip
      size="small"
      variant="outlined"
      color={colorMap[status] ?? 'default'}
      label={MIX_STOCK_STATUS_LABELS[status] ?? status}
    />
  );
}

function getEventLabel(event) {
  if (event.eventType === 'STATUS_CHANGED') {
    return `${
      MIX_DELIVERY_STATUS_LABELS[event.previousStatus] ?? event.previousStatus
    } → ${MIX_DELIVERY_STATUS_LABELS[event.newStatus] ?? event.newStatus}`;
  }

  const labels = {
    IMPORTED: 'Dostawa została zaimportowana',

    DATA_CHANGED: 'Dane dostawy zostały zmienione',

    DEACTIVATED: 'Dostawa została dezaktywowana',

    REACTIVATED: 'Dostawa została ponownie aktywowana',

    CHANGE_REVIEW_REQUIRED: 'Wykryto zmianę po przygotowaniu',

    CHANGE_REVIEW_CONFIRMED: 'Zmiana została potwierdzona',

    REOPENED: 'Dostawa została ponownie otwarta do przygotowania',
  };

  return labels[event.eventType] ?? event.eventType;
}
function isMixItem(item) {
  if (typeof item?.isMixPosition === 'boolean') {
    return item.isMixPosition;
  }

  return Number(item?.mixQuantity ?? 0) > 0;
}

function formatQuantity(value) {
  const numberValue = Number(value ?? 0);

  return new Intl.NumberFormat('pl-PL', {
    maximumFractionDigits: 3,
  }).format(numberValue);
}

function formatDate(value) {
  if (!value) {
    return '—';
  }

  return new Intl.DateTimeFormat('pl-PL').format(new Date(value));
}

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  return new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

const OPERATIONAL_CHANGE_LABELS = {
  MATERIAL_NUMBER: 'Zmieniono materiał',
  UNIT: 'Zmieniono jednostkę',
  DELIVERY_QUANTITY: 'Zmieniono ilość dostawy',
  FULL_QUANTITY: 'Zmieniono ilość pełnopaletową',
  MIX_QUANTITY: 'Zmieniono ilość miksową',
  MIX_CLASSIFICATION: 'Zmieniono klasyfikację miks/pełna paleta',
  ORIGINAL_STORAGE_LOCATION: 'Zmieniono skład w dostawie',
};

const STOCK_CHANGE_LABELS = {
  RECOMMENDED_STORAGE_LOCATION: 'Zmieniono rekomendowany skład',

  RECOMMENDED_STORAGE_LOCATION_APPLIED: 'Wykonano rekomendowaną zmianę składu',

  STOCK_STATUS: 'Zmieniono status zapasu',

  REPLENISHMENT_QUANTITY: 'Zmieniono ilość do uzupełnienia',

  SHORTAGE_QUANTITY: 'Zmieniono ilość brakującą',
};

function ChangedPositionSummary({ item }) {
  const operationalChanges = item.operationalChanges ?? [];

  const stockChanges = item.stockChanges ?? [];
  const valueChanges = item.valueChanges ?? [];

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 1,
        bgcolor: 'warning.50',
      }}
    >
      <Typography variant="body2" fontWeight={700}>
        Pozycja {item.positionNumber}
        {' · '}
        materiał {item.materialNumber}
      </Typography>
      {valueChanges.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          {valueChanges.map((change, index) => (
            <Box
              key={`${change.code}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {CHANGE_FIELD_LABELS[change.code] ?? change.code}:
              </Typography>

              <Chip
                size="small"
                variant="outlined"
                label={formatChangedValue(
                  change.code,
                  change.previousValue,
                  item.unit,
                )}
              />

              <Typography variant="body2" fontWeight={700}>
                →
              </Typography>

              <Chip
                size="small"
                color={
                  change.category === 'OPERATIONAL' ? 'warning' : 'default'
                }
                label={formatChangedValue(
                  change.code,
                  change.currentValue,
                  item.unit,
                )}
              />
            </Box>
          ))}
        </Stack>
      )}
      {operationalChanges.length > 0 && (
        <Stack
          direction="row"
          spacing={0.5}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 0.75 }}
        >
          {operationalChanges.map((change) => (
            <Chip
              key={change}
              size="small"
              color="warning"
              label={OPERATIONAL_CHANGE_LABELS[change] ?? change}
            />
          ))}
        </Stack>
      )}

      {stockChanges.length > 0 && (
        <Stack
          direction="row"
          spacing={0.5}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 0.75 }}
        >
          {stockChanges.map((change) => (
            <Chip
              key={change}
              size="small"
              variant="outlined"
              label={STOCK_CHANGE_LABELS[change] ?? change}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

const CHANGE_FIELD_LABELS = {
  MATERIAL_NUMBER: 'Materiał',

  UNIT: 'Jednostka',

  DELIVERY_QUANTITY: 'Ilość dostawy',

  FULL_QUANTITY: 'Ilość pełnopaletowa',

  MIX_QUANTITY: 'Ilość miksowa',

  MIX_CLASSIFICATION: 'Rodzaj pozycji',

  ORIGINAL_STORAGE_LOCATION: 'Skład w dostawie',

  RECOMMENDED_STORAGE_LOCATION: 'Rekomendowany skład',

  RECOMMENDED_STORAGE_LOCATION_APPLIED: 'Wykonana zmiana składu',

  STOCK_STATUS: 'Status zapasu',

  REPLENISHMENT_QUANTITY: 'Ilość do uzupełnienia',

  SHORTAGE_QUANTITY: 'Ilość brakująca',
};

const QUANTITY_CHANGE_CODES = new Set([
  'DELIVERY_QUANTITY',
  'FULL_QUANTITY',
  'MIX_QUANTITY',
  'REPLENISHMENT_QUANTITY',
  'SHORTAGE_QUANTITY',
]);

function formatChangedValue(code, value, unit) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (code === 'MIX_CLASSIFICATION') {
    return value ? 'Pozycja miksowa' : 'Pozycja pełnopaletowa';
  }

  if (code === 'STOCK_STATUS') {
    return MIX_STOCK_STATUS_LABELS[value] ?? String(value);
  }

  if (QUANTITY_CHANGE_CODES.has(code)) {
    const formatted = formatQuantity(value);

    return unit ? `${formatted} ${unit}` : formatted;
  }

  return String(value);
}
